import React, { useState, KeyboardEvent } from 'react';
import type { Answers, BenefitKey, Tone, WizardQuestion } from './types';
import { BENEFIT_OPTIONS, TONE_OPTIONS } from './questions';

interface WizardStepProps {
  question: WizardQuestion;
  value: Answers[keyof Answers];
  onChange: (value: Answers[keyof Answers]) => void;
}

export const WizardStep: React.FC<WizardStepProps> = ({ question, value, onChange }) => {
  const inputId = `dide-q-${question.id}`;

  const labelBlock = (
    <>
      <label htmlFor={inputId} className="form-label fw-semibold">
        {question.label}
        {!question.required && <span className="text-muted ms-1">(optional)</span>}
      </label>
      {question.helper && <div id={`${inputId}-helper`} className="form-text mb-2">{question.helper}</div>}
    </>
  );

  if (question.type === 'textarea') {
    const text = typeof value === 'string' ? value : '';
    return (
      <div className="mb-3">
        {labelBlock}
        <textarea
          id={inputId}
          className="form-control"
          rows={3}
          value={text}
          maxLength={question.maxLength ?? 500}
          placeholder={question.placeholder}
          aria-describedby={question.helper ? `${inputId}-helper` : undefined}
          onChange={e => onChange(e.target.value)}
        />
        <div className="form-text text-end">{text.length} / {question.maxLength ?? 500}</div>
      </div>
    );
  }

  if (question.type === 'text') {
    const text = typeof value === 'string' ? value : '';
    return (
      <div className="mb-3">
        {labelBlock}
        <input
          id={inputId}
          className="form-control"
          type="text"
          value={text}
          maxLength={question.maxLength ?? 200}
          placeholder={question.placeholder}
          onChange={e => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (question.type === 'chips') {
    return <ChipsInput question={question} value={Array.isArray(value) ? value : []} onChange={onChange} inputId={inputId} labelBlock={labelBlock} />;
  }

  if (question.type === 'tone') {
    const selected = (value as Tone | undefined) ?? 'professional';
    return (
      <fieldset className="mb-3" aria-labelledby={`${inputId}-legend`}>
        <legend id={`${inputId}-legend`} className="form-label fs-6 fw-semibold mb-1">{question.label}</legend>
        {question.helper && <div className="form-text mb-2">{question.helper}</div>}
        <div role="radiogroup" className="d-flex flex-wrap gap-2">
          {TONE_OPTIONS.map(opt => {
            const active = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                title={opt.hint}
                className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => onChange(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div className="form-text" aria-live="polite">
          {TONE_OPTIONS.find(opt => opt.value === selected)?.hint}
        </div>
      </fieldset>
    );
  }

  if (question.type === 'benefits') {
    const selected = (Array.isArray(value) ? (value as BenefitKey[]) : []);
    const toggle = (key: BenefitKey) => {
      onChange(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]);
    };
    return (
      <fieldset className="mb-3">
        <legend className="form-label fs-6 fw-semibold mb-1">{question.label}</legend>
        {question.helper && <div className="form-text mb-2">{question.helper}</div>}
        <div className="d-flex flex-wrap gap-2">
          {BENEFIT_OPTIONS.map(opt => {
            const active = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={active}
                // Same reasoning as Q2 suggestion chips: use the success
                // family for both states so the text color is locked to a
                // defined-contrast token instead of inheriting from the
                // surrounding modal CSS.
                className={`btn btn-sm ${active ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => toggle(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  return null;
};

interface ChipsInputProps {
  question: WizardQuestion;
  value: string[];
  onChange: (value: string[]) => void;
  inputId: string;
  labelBlock: React.ReactNode;
}

const ChipsInput: React.FC<ChipsInputProps> = ({ question, value, onChange, inputId, labelBlock }) => {
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    const cleaned = raw.trim();
    if (!cleaned) return;
    if (question.maxItems && value.length >= question.maxItems) return;
    if (value.includes(cleaned)) {
      setDraft('');
      return;
    }
    onChange([...value, cleaned]);
    setDraft('');
  };

  const remove = (item: string) => {
    onChange(value.filter(v => v !== item));
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <div className="mb-3">
      {labelBlock}
      <div className="d-flex flex-wrap align-items-center gap-2 border rounded p-2">
        {value.map(item => (
          // The host page (`application.html.erb`) ships Bootstrap 5.1.3,
          // which predates the `text-bg-*` color-pair utilities (added in
          // 5.2). Without them, our form-page `.badge` SCSS rule paints grey
          // text on a grey gradient and the chip text vanishes. Use the
          // older-compatible `bg-primary text-white` combo with `!important`
          // so we override the form `.badge` background gradient too.
          <span
            key={item}
            className="badge bg-primary text-white d-inline-flex align-items-center"
            style={{ background: '#0d6efd', border: 'none' }}
          >
            {item}
            <button
              type="button"
              aria-label={`Remove ${item}`}
              className="btn-close btn-close-white ms-2"
              style={{ fontSize: '0.6rem' }}
              onClick={() => remove(item)}
            />
          </span>
        ))}
        <input
          id={inputId}
          type="text"
          className="border-0 flex-grow-1"
          style={{ outline: 'none', minWidth: 120 }}
          value={draft}
          placeholder={value.length === 0 ? 'Type and press Enter…' : ''}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKey}
          onBlur={() => add(draft)}
        />
      </div>
      {question.suggestions && question.suggestions.length > 0 && (
        <div className="mt-2">
          <div className="form-text mb-1">Suggestions:</div>
          <div className="d-flex flex-wrap gap-2">
            {question.suggestions
              .filter(s => !value.includes(s))
              .map(s => (
                // Use `btn-outline-primary` (same family as the selected chip's
                // `text-bg-primary`) so the suggestion → chip transition stays
                // visually coherent. Avoiding `btn-outline-secondary` here on
                // purpose: a host-modal override elsewhere in the app paints
                // its `color` white, making the suggestion text invisible
                // until clicked.
                <button
                  key={s}
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => add(s)}
                >
                  + {s}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WizardStep;
